
import React from 'react';

// Default styled components that work without external dependencies
export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: string }> = ({ 
  children, 
  className = '', 
  variant = 'default',
  ...props 
}) => {
  const baseClasses = 'px-4 py-2 rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variantClasses = variant === 'outline' 
    ? 'border-2 border-input text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-ring'
    : 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-ring';
  
  return (
    <button 
      className={`${baseClasses} ${variantClasses} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ 
  className = '', 
  ...props 
}) => (
  <input 
    className={`w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${className}`}
    {...props}
  />
);

export const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ 
  className = '', 
  ...props 
}) => (
  <textarea 
    className={`w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring resize-vertical ${className}`}
    {...props}
  />
);

export const Label: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ 
  className = '', 
  children,
  ...props 
}) => (
  <label 
    className={`block text-sm font-medium text-foreground mb-1 ${className}`}
    {...props}
  >
    {children}
  </label>
);

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { 
  placeholder?: string;
  onValueChange?: (value: string) => void;
}> = ({ 
  className = '', 
  placeholder,
  onValueChange,
  onChange,
  children,
  ...props 
}) => (
  <select 
    className={`w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${className}`}
    onChange={(e) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    }}
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {children}
  </select>
);

export const Checkbox: React.FC<React.InputHTMLAttributes<HTMLInputElement> & {
  onCheckedChange?: (checked: boolean) => void;
}> = ({ 
  className = '', 
  onCheckedChange,
  onChange,
  ...props 
}) => (
  <input 
    type="checkbox"
    className={`h-4 w-4 text-primary focus:ring-ring border-input rounded ${className}`}
    onChange={(e) => {
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    }}
    {...props}
  />
);

export const Switch: React.FC<React.InputHTMLAttributes<HTMLInputElement> & {
  onCheckedChange?: (checked: boolean) => void;
}> = ({ 
  className = '', 
  onCheckedChange,
  onChange,
  ...props 
}) => (
  <label className={`relative inline-flex items-center cursor-pointer ${className}`}>
    <input 
      type="checkbox"
      className="sr-only"
      onChange={(e) => {
        onChange?.(e);
        onCheckedChange?.(e.target.checked);
      }}
      {...props}
    />
    <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
  </label>
);

export const RadioGroup: React.FC<{
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ value, onValueChange, children, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          ...child.props,
          checked: child.props.value === value,
          onChange: () => onValueChange?.(child.props.value),
        });
      }
      return child;
    })}
  </div>
);

export const RadioGroupItem: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ 
  className = '', 
  ...props 
}) => (
  <input 
    type="radio"
    className={`h-4 w-4 text-primary focus:ring-ring border-input ${className}`}
    {...props}
  />
);

export { TableInput } from './TableInput';
